import { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { IconSearch } from '@tabler/icons-react';

import ClassSummaryCard from '@/components/ClassSummaryCard';
import EmptyState from '@/components/EmptyState';
import Input from '@/components/Input/Input';
import Pagination from '@/components/Pagination';
import AssignedClassesPageSkeleton from '@/components/SkeletonLoader/AssignedClassesPageSkeleton';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getTeacherClassList } from '@/service/classService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { ITeacherClass } from '@/types/class';

const TeacherAssignedClasses = () => {
  const navigate = useNavigate();
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();
  const { isScreenLoading, setScreenLoading } = useSkeletonStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 12;

  const [classes, setClasses] = useState<ITeacherClass[]>([]);
  const isInitialLoad = useRef(true);

  const { register, watch } = useForm({
    defaultValues: {
      search: '',
    },
  });

  const search = watch('search');
  const debouncedSearch = useDebounce(search, 500);

  const pageTitle = formatText('dashboard.teacher-dashboard.assigned-classes');

  const breadcrumbItems = [
    { label: formatText('dashboard.title'), to: '/teacher/dashboard' },
    {
      label: pageTitle,
      to: '',
    },
  ];

  useEffect(() => {
    setTitle(pageTitle);
    setIsBreadcrumb(breadcrumbItems);
    return () => {
      setTitle('');
      setIsBreadcrumb([]);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const fetchClasses = async () => {
    const isFirstLoad = isInitialLoad.current;
    if (isFirstLoad) setScreenLoading(true);
    try {
      const response = await getTeacherClassList({
        ...(debouncedSearch && { search: debouncedSearch }),
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });
      if (response?.status) {
        setClasses(response.data || []);
        setTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching assigned classes:', error);
    } finally {
      setScreenLoading(false);
      isInitialLoad.current = false;
    }
  };

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, currentPage]);

  if (isScreenLoading) {
    return <AssignedClassesPageSkeleton />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        <div className="max-w-md ps-1">
          <Input
            name="search"
            register={register}
            placeholder={formatText(
              'dashboard.teacher-dashboard.search-by-class'
            )}
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-400" />,
            }}
            className="max-w-75"
            inputClassName="py-[13px]"
          />
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold text-neutral-primary ps-1">
            {pageTitle}
          </h2>
          {classes.length > 0 ? (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {classes.map((cls, index) => (
                  <ClassSummaryCard
                    key={cls.id}
                    name={cls.name}
                    studentsCount={cls.active_students}
                    lessonsCount={cls.lesson_created}
                    colorIndex={index}
                    className="cursor-pointer hover:shadow-sm max-w-full!"
                    onClick={() => navigate(`/teacher/classes/${cls.id}`)}
                  />
                ))}
              </div>
              {total > pageSize && (
                <Pagination
                  total={total}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  className="border-t-0!"
                />
              )}
            </div>
          ) : (
            <EmptyState
              title={formatText('common.no-data-found')}
              className="h-75"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherAssignedClasses;
